/**
 * Die **Wohneinheiten** eines Mieterstrom-Konzepts, gebaut wie die
 * „Zusammensetzung des Verbrauchs" (HM-145): ein Balken über alle Wohnungen,
 * dann je Wohnung eine Zeile — Farbmarke, Name, ihr Zähler, Verbrauch und
 * Anteil. Gerechnet ist in `lib/units-composition.ts`. Seit HM-150 steht unter
 * Wert und Leistung die Verteilung auf PV-Anlage und Netz, wie in der
 * Zusammensetzung.
 *
 * Bis dahin war es eine Tabelle mit drei Zahlenspalten — Verbrauch,
 * PV-Anteil, Netzanteil —, und die Frage „wie verteilt sich das Haus auf die
 * Wohnungen" beantwortete erst, wer die Zahlen im Kopf verglich. Jetzt stehen
 * dieselben Zahlen in derselben Form wie eine Karte darüber, und dieselbe
 * Wohnung trägt dieselbe Stufe wie im Stapel „Verbrauch".
 *
 * **Ein Verbraucher hinter dem Zähler einer Wohnung** (HM-111) steht unter
 * ihrem Namen, als Teil ihres Verbrauchs — keine eigene Zeile, die sich zu den
 * anderen addiert.
 *
 * **Seit HM-146 wie der überarbeitete Entwurf der Zusammensetzung**: die Summe
 * über dem Balken und, solange der Zeitraum das Jetzt enthält, die Leistung
 * jetzt je Wohnung — aus ihrem Zähler.
 *
 * **Seit dem Nachtrag zu HM-150 ist die Karte ruhiger** (Auftraggeber): Unter
 * dem Titel steht nur der Zeitraum, der erklärende Absatz am Fuß ist entfallen,
 * und dort steht stattdessen die Legende für PV-Anlage und Netz
 * (`OriginLegend`) — einmal für die ganze Karte.
 */
import { Badge, Card, quantityColorVar, useNarrow } from "@hausmeter/ui";
import type { ReactNode } from "react";
import {
  ENERGY_UNIT,
  formatEnergy,
  formatPercent,
  totalNote,
} from "../lib/energy-format.js";
import type { CompositionLive } from "../lib/composition-live.js";
import type { UnitCompositionRow, UnitsComposition } from "../lib/units-composition.js";
import {
  compositionColumns,
  LiveBar,
  LiveCell,
  liveOriginOf,
  MONO,
  OriginLegend,
  originPairOf,
  periodSubtitle,
  TotalHeader,
  ValueCell,
  type PartMeter,
} from "./consumption-composition.js";

/** Die Stufe einer Wohnung — dieselbe Mischung mit dem Kartengrund wie im Diagramm. */
const shadeOf = (fade: number): string =>
  fade <= 0
    ? quantityColorVar("consumption")
    : `color-mix(in srgb, ${quantityColorVar("consumption")} ${Math.round((1 - fade) * 100)}%, var(--surface-card))`;

export function UnitsCompositionCard({
  composition,
  caption,
  meterOf,
  linkOf,
  live = null,
}: {
  composition: UnitsComposition;
  caption: string;
  meterOf: (row: UnitCompositionRow) => PartMeter | null;
  /** Der Name als Link zur Wohnung — `null`, wo es keine Seite gibt. */
  linkOf: (row: UnitCompositionRow, label: ReactNode) => ReactNode;
  live?: CompositionLive | null;
}) {
  const narrow = useNarrow(720);
  const { rows, sum } = composition;
  const note = totalNote(composition);
  /* Die Legende nur, wo Blatt und Blitz irgendwo in der Karte stehen. */
  const hasOrigin =
    originPairOf(composition.origin) !== null ||
    rows.some((unit) => originPairOf(unit.origin) !== null) ||
    (live !== null && liveOriginOf(live, null) !== null);

  const row = (unit: UnitCompositionRow) => {
    const meter = meterOf(unit);
    const name = (
      <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }}>
        {unit.label}
      </span>
    );
    return (
      <div
        key={unit.key}
        role="row"
        style={{
          display: "grid",
          gridTemplateColumns: compositionColumns(narrow, live !== null),
          gap: 14,
          alignItems: "center",
          padding: "12px 0",
          borderTop: "1px solid var(--border-subtle)",
        }}
      >
        <span role="cell" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          {unit.fade !== null && (
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                flexShrink: 0,
                background: shadeOf(unit.fade),
              }}
            />
          )}
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block" }}>{linkOf(unit, name)}</span>
            {unit.consumers.length > 0 && (
              <span
                title="im Verbrauch der Wohnung enthalten"
                style={{ display: "block", fontSize: 11.5, color: "var(--text-faint)" }}
              >
                darin{" "}
                {unit.consumers
                  .map(
                    (consumer) =>
                      `${consumer.label}${consumer.total?.value == null ? "" : ` ${formatEnergy(consumer.total.value)} ${ENERGY_UNIT}`}`,
                  )
                  .join(" · ")}
              </span>
            )}
            {narrow && meter !== null && (
              <span style={{ display: "block", fontSize: 11.5, color: "var(--text-muted)" }}>
                {meter.label} · <span style={MONO}>{meter.number}</span>
              </span>
            )}
          </span>
        </span>
        {!narrow && (
          <span role="cell" style={{ minWidth: 0 }}>
            {meter !== null && (
              <>
                <span style={{ display: "block", fontSize: 12.5, color: "var(--text-body)" }}>
                  {meter.label}
                </span>
                <span
                  style={{ ...MONO, display: "block", fontSize: 11.5, color: "var(--text-muted)" }}
                >
                  {meter.number}
                </span>
              </>
            )}
          </span>
        )}
        {live !== null && (
          <LiveCell
            watts={live.byKey.get(unit.key) ?? 0}
            rest={false}
            origin={liveOriginOf(live, unit.key)}
          />
        )}
        <ValueCell valueWh={unit.total?.value} share={unit.share} origin={originPairOf(unit.origin)} />
      </div>
    );
  };

  return (
    <Card
      title="Wohneinheiten"
      subtitle={periodSubtitle(caption)}
      action={
        <Badge tone="neutral">
          {rows.length === 1 ? "1 Wohneinheit" : `${rows.length} Wohneinheiten`}
        </Badge>
      }
    >
      {composition.stackable && sum !== null && (
        <>
          <TotalHeader
            label="Wohneinheiten zusammen"
            valueWh={sum}
            note={note}
            measuredShare={null}
            origin={originPairOf(composition.origin)}
          />
          <div
            role="img"
            aria-label={rows
              .filter((unit) => unit.share !== null)
              .map((unit) => `${unit.label} ${formatPercent(unit.share!)}`)
              .join(", ")}
            style={{
              display: "flex",
              height: 34,
              borderRadius: "var(--radius-sm)",
              overflow: "hidden",
              background: "var(--surface-sunken)",
            }}
          >
            {rows.map((unit) =>
              unit.share === null || unit.share <= 0 || unit.fade === null ? null : (
                <span
                  key={unit.key}
                  title={`${unit.label} · ${formatEnergy(unit.total!.value!)} ${ENERGY_UNIT} · ${formatPercent(unit.share)}`}
                  style={{ flex: `${unit.share} 0 0`, minWidth: 2, background: shadeOf(unit.fade) }}
                />
              ),
            )}
          </div>
          {live !== null && (
            <LiveBar
              live={live}
              segments={rows.map((unit) => ({
                key: unit.key,
                label: unit.label,
                color: shadeOf(unit.fade ?? 0),
              }))}
            />
          )}
        </>
      )}

      <div role="table" aria-label="Wohneinheiten" style={{ marginTop: 14 }}>
        {rows.map(row)}
      </div>

      {hasOrigin && <OriginLegend />}
    </Card>
  );
}
